import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Construction } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logoFallback from "../../assets/digited_logo.png";
import logoMobileFallback from "../../assets/logoMobile.png";
import { ButtonText, GuardedLink, Icon, Logo, Spinner } from "../../components";
import { getSmartConfiguration, persistAuthStore } from "../../services/auth";
import { getMaintenanceStatus } from "../../services/maintenance";
import { useAuthStore } from "../../stores";
import { cleanAllCookies } from "../../utils/cookies";
import { getEnvironmentVariables } from "../../utils/general";


export const Maintenance = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = useAuthStore(state => state.userData);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const signOut = useAuthStore(state => state.signOut);
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const maintenanceGlobal = useAuthStore(state => state.maintenanceGlobal);
  const setMaintenanceGlobal = useAuthStore(state => state.setMaintenanceGlobal);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const [updatedMaintenanceInitiative, setUpdatedMaintenanceInitiative] = useState(
    smartConfiguration?.showMaintenancePage ?? false
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false); // Stato per tracking logout
  document.getElementById("lds-ring")?.remove();
  const inititiveUrl =  smartConfiguration?.urlDomain
    ? `${smartConfiguration?.urlDomain}.${smartConfiguration.urlCorporateDomain}`
    : smartConfiguration?.urlCorporateDomain
      ?  `${smartConfiguration?.urlCorporateDomain}`
      : window.location.host;

  // Funzione di logout con redirect diretta (come nel loader /logout)
  const handleLogout = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();

    // Imposta stato di loading per evitare flash di "manutenzione non attiva"
    setIsLoggingOut(true);

    try {
      // 1. Pulisci i cookie (come nell'header)
      cleanAllCookies(["activeLangCode", "languageStored"]);

      // 2. Pulisci lo stato auth direttamente (senza delegare redirect)
      await signOut(null, false, false); // null = non delegare redirect, false = non admin, false = non saveFromUrl

      // 3. Redirect diretto al login (come nel loader /logout)
      window.location.href = "/access/login";
    } catch (error) {
      // In caso di errore, rimuovi lo stato di loading
      setIsLoggingOut(false);
    }
  }, [signOut]);

  useEffect(() => {

    const checkMaintenance = async () => {
      if (!userData?.roles || !idToken || !sessionToken) return;

      try {
        const maintenanceParams = {
          idToken: idToken,
          sessionToken: sessionToken,
          userRoles: userData?.roles
        };
        const enabled = await getMaintenanceStatus(maintenanceParams);
        setMaintenanceGlobal(enabled);

        let showInitiativeMaintenance = false;
        if (userData?.initiative_id){
          try {
            const refreshedSmartConfiguration = await getSmartConfiguration({
              accessToken: idToken,
              corporateId: userData?.organization_id,
              initiativeId: userData?.initiative_id,
              sessionToken: sessionToken
            });
            showInitiativeMaintenance = refreshedSmartConfiguration?.showMaintenancePage ?? false;
            setUpdatedMaintenanceInitiative(showInitiativeMaintenance);

            useAuthStore.setState({ smartConfiguration: refreshedSmartConfiguration ?? null });
          } catch (smartConfigError) {
            // Gestione errore 401 anche per getSmartConfiguration
            if (smartConfigError === "UNAUTHORIZED" ||
                (typeof smartConfigError === "string" && smartConfigError.includes("401"))) {
              const fakeEvent = { preventDefault: () => {} } as React.MouseEvent;
              handleLogout(fakeEvent);
              return;
            }
            // Per altri errori, continua normalmente
          }
        }

        persistAuthStore(useAuthStore.getState(), () => {
          useAuthStore.getState().signOut("/access/login", false, false);
        });

        if (!enabled && !showInitiativeMaintenance) {
          window.location.href = "/";
        }
      } catch (error) {
        // Gestione errore 401 - sessione scaduta per getMaintenanceStatus
        if (error === "UNAUTHORIZED" || (typeof error === "string" && error.includes("401"))) {
          const fakeEvent = { preventDefault: () => {} } as React.MouseEvent;
          handleLogout(fakeEvent);
        }
        // Per altri errori, non fare nulla (comportamento silenzioso)
      }
    };
    checkMaintenance();
  }, [userData, sessionToken, idToken, setMaintenanceGlobal, handleLogout]);


  const { digitedLogo, digidetLogoMobile, digitedLogoHeight } = getEnvironmentVariables();

  return <div style={ { height: "100vh", display: "flex", flexDirection: "column", boxSizing: "border-box" } }>
    <header style={ { display:"flex", alignItems:"center", padding: "1rem 2.5rem", height: "5rem", background: theme.customColors.backgroundPrimaryLighterAlternative } }>
      <Logo
        alt="logo"
        width="auto"
        // eslint-disable-next-line max-len
        height={ digitedLogoHeight ? digitedLogoHeight + "px" : "32px" }
        src={ isMobile ? digidetLogoMobile ?? logoMobileFallback : digitedLogo ?? logoFallback }
      />
    </header>
    { !(!maintenanceGlobal && !updatedMaintenanceInitiative && !isLoggingOut) &&
      <div style={ { padding: "1rem 2.5rem" } }>
        <GuardedLink
          alignSelf="flex-start"
          color="info"
          onClick={ handleLogout }
          variant="text"
          disabled={ isLoggingOut }
          startIcon={
            <Icon
              color={ theme.customColors.systemPrimary01 }
              icon="arrow_left"
              size={ 24 }
            />
          }
          sx={ {
            fontSize: (isMobile ? "0.75rem" : "1.125rem"),
            fontWeight: 400,
            lineHeight: isMobile ? (11 / 6) : (11 / 9),
            minWidth: isMobile ? "auto" : "128px",
            padding: isMobile ? "16px 0 0 0" : undefined,
            opacity: isLoggingOut ? 0.6 : 1
          } }
        >
          { t("back") }
        </GuardedLink>
      </div>
    }
    <section style={ {
      alignItems: "center",
      display: "flex",
      flex: "1 0 auto",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: "calc(100vh - 5rem -2.5rem)",
      height: "calc(100vh - 5rem -2.5rem)"
    } }>
      { /* PRIORITÀ: Manutenzione globale ha precedenza su quella per iniziativa */ }
      { maintenanceGlobal ? (
        // Mostra SOLO manutenzione globale (ha priorità)
        <Stack direction="row" display="flex" gap="5rem" justifyContent="center" flexWrap="wrap">
          <Construction size="8.5rem" color={ theme.customColors.backgroundPrimaryCta } style={ {
            minWidth:"8rem"
          } } />
          <div style={ { alignContent: "center" } }>
            <Typography
              fontSize={ "2rem" }
              fontWeight={ 700 }
              color={ theme.customColors.textPrimary }
              textAlign={ "center" }
              sx={ { marginBottom: "20px" } }
            >
              { t("maintenance-title") }
            </Typography>
            <Typography
              fontSize={ "1rem" }
              fontWeight={ 700 }
              color={ theme.customColors.textPrimary }
              textAlign={ "center" }
            >
              { t("maintenance-description") }
            </Typography>
          </div>
        </Stack>
      ) : updatedMaintenanceInitiative ? (
        // Mostra manutenzione per iniziativa SOLO se non c'è quella globale
        <div style={ { width: "100%" } }>
          <iframe src={ `https://${inititiveUrl}/maintenance-landing-page/Maintenance.html` } title={ t("maintenance-title") } style={ {
            border: 0,
            width: "100%",
            height: "calc(100vh - 110px)"
          } }></iframe>
        </div>
      ) : isLoggingOut ? (
        // Mostra spinner durante logout
        <Spinner
          size={ 200 }
          thickness={ 3 }
        />
      ) : (
        // Mostra messaggio "manutenzione non attiva" solo se nessuna manutenzione è attiva
        <div style={ { alignContent: "center", width: "100%" } }>
          <Typography
            fontSize={ "2rem" }
            fontWeight={ 700 }
            color={ theme.customColors.textPrimary }
            textAlign={ "center" }
            sx={ { marginBottom: "20px" } }
          >
            { t("maintenance-not-active") }
          </Typography>
          <Stack alignItems={ "center" } direction={ "column" } gap={ 3 } width={ "100%" }>
            <ButtonText onClick={ handleLogout } disabled={ isLoggingOut }>
              { t("back_to_login") }
            </ButtonText>
          </Stack>
        </div>
      ) }
    </section>
  </div>;
};
